import { useState } from 'react'

import { Box, Item } from '@factorialco/deprecated-design-system'
import { LedgerAccountFragment } from 'generated/resources/graphql'
import { compact } from 'lodash'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useLedgerAccountResources } from './useLedgerAccountResources'

export function formatAccountName(
  account?: { name?: string | null; number?: string | null } | null
) {
  if (!account) return null
  return compact([account?.number ?? '', account?.name ?? '']).join(' - ')
}

interface LedgerAccountSelectorProps {
  legalEntityId: string
  /** This is the current existing relation */
  currentLedgerAccountName?: string | null
  /** Filter of types to show in the selector, normaly should be one: 'bank', 'expense',... */
  types?: string[]
  label?: string
  /** Accounts if has been fetched previously */
  accounts?: LedgerAccountFragment[]
  /** Permission if has been fetched previously */
  canManage?: boolean

  /** Preselected id */
  selectedLedgerAccountIdProp?: string

  disabled: boolean
  onSelect: (ledgerAccountId: string) => void
  onEntitySelect?: (entity?: LedgerAccountFragment) => void

  hideBorder?: boolean
  /** ids of accounts to not show on the selector */
  skipLedgerAccountIds?: string[]
}

export const LedgerAccountSelector = ({
  legalEntityId,
  currentLedgerAccountName,
  label = '',
  types,
  disabled,
  onSelect,
  onEntitySelect,
  canManage,
  accounts,
  selectedLedgerAccountIdProp,
  hideBorder = false,
  skipLedgerAccountIds,
}: LedgerAccountSelectorProps) => {
  const [selectedLedgerAccountId, setSelectedLedgerAccountId] = useState<string | undefined>(
    selectedLedgerAccountIdProp
  )
  const {
    loadingPage: loading,
    accounts: fetchedAccounts,
    canManage: fetchCanManage,
  } = useLedgerAccountResources({
    legalEntityId: legalEntityId,
    types,
    skip: !!accounts && !!canManage,
  })

  useEffect(() => {
    if (selectedLedgerAccountIdProp) {
      setSelectedLedgerAccountId(selectedLedgerAccountIdProp)
    }
  }, [selectedLedgerAccountIdProp])

  const accountsList = useUnsafeMemo(
    () =>
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      (accounts ?? fetchedAccounts).filter(
        (account) => !skipLedgerAccountIds?.includes(account.id)
      ) as LedgerAccountFragment[],
    [accounts, fetchedAccounts, skipLedgerAccountIds]
  )

  const canManageResult = useUnsafeMemo(
    () => canManage ?? fetchCanManage,
    [canManage, fetchCanManage]
  )

  const selectedLedgerAccount = useUnsafeMemo(
    () => accountsList.find((a) => a.id === selectedLedgerAccountId),
    [accountsList, selectedLedgerAccountId]
  )

  const onSelectInterceptor = useUnsafeCallback(
    (item: Item) => {
      const id = String(item.value)
      setSelectedLedgerAccountId(id)
      onEntitySelect?.(accountsList.find((a) => a.id === id))
      onSelect(id)
    },
    [onSelect, onEntitySelect, accountsList]
  )

  return (
    <Box>
      <DropdownSearchBar
        name={formatAccountName(selectedLedgerAccount) ?? currentLedgerAccountName ?? ''}
        data={accountsList.map((o) => ({
          label: formatAccountName(o) + '',
          value: o.id,
        }))}
        label={label}
        labelNotFound={i18n.t('banking.accounts.accounting.accounting_accounts_empty')}
        searchLabel={i18n.t('common.search')}
        sortLabel={i18n.t('common.name')}
        placeholder={i18n.t('banking.accounts.accounting.accounting_account_hint')}
        selectLabel={i18n.t('finance.settings.accounting.expense_category.form.select')}
        disabled={disabled || !canManageResult}
        autofocus={false}
        loading={loading}
        onSelect={onSelectInterceptor}
        hideBorder={hideBorder}
      />
    </Box>
  )
}
